import { REDACTED } from './redactSensitiveUrl';

/**
 * Keep the photobook bio token out of the address bar.
 *
 * The token in `/{locale}/photobook/bio/{token}` is a credential. As soon as
 * the page loads it is moved into sessionStorage (per tab, so two links in
 * two tabs stay apart) and the address bar is rewritten to
 * `/{locale}/photobook/bio/your-pet`. Nothing that reads the URL later
 * (analytics, ad tags, the referrer on an outbound click, browser history)
 * sees it, and a reload still works because the page reads the stored token
 * back when the path shows the marker.
 *
 * Analytics redaction (redactSensitiveUrl) uses its own marker,
 * `[redacted]`. Neither can be a real token: tokens always contain a ".".
 */

export const BIO_TOKEN_KEY = 'pupvote_photobook_bio_token';

/** What the address bar shows in place of the token. */
export const BIO_PATH_MARKER = 'your-pet';

const BIO_PATH = /^\/[^/]+\/photobook\/bio\/([^/?#]+)\/?$/;

const decode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
};

/** True for a path segment that stands in for a token (current or older marker). */
export function isBioMarker(value) {
  return value === BIO_PATH_MARKER || value === REDACTED;
}

/** The token in a bio path, or null when there is none (or only a marker). */
export function bioTokenInPath(pathname) {
  const match = String(pathname || '').match(BIO_PATH);
  if (!match) return null;

  const token = decode(match[1]);

  return token && !isBioMarker(token) ? token : null;
}

/** The same path with the token swapped for the visible marker. */
export function visibleBioPath(pathname) {
  return String(pathname || '').replace(/(\/photobook\/bio\/)[^/?#]+/, `$1${BIO_PATH_MARKER}`);
}

function storage(win) {
  try {
    return win.sessionStorage || null;
  } catch (e) {
    return null;
  }
}

export function storedBioToken(win = window) {
  try {
    const store = storage(win);
    return (store && store.getItem(BIO_TOKEN_KEY)) || null;
  } catch (e) {
    return null;
  }
}

export function rememberBioToken(token, win = window) {
  try {
    const store = storage(win);
    if (store) store.setItem(BIO_TOKEN_KEY, token);
  } catch (e) {
    // Private mode with storage off: the page still works, a reload won't.
  }
}

/**
 * The token for a route param: the param itself, or the stored one when
 * the address bar already shows a marker (`your-pet`, or the older
 * `[redacted]`). Null when there is none.
 */
export function resolveBioToken(param, win = window) {
  const value = decode(param ? String(param) : '');

  if (!value || isBioMarker(value)) return storedBioToken(win);

  return value;
}

/**
 * Move a bio token from the address bar into sessionStorage. Run once at
 * startup, before the router reads the URL and before any tag manager or
 * pixel is initialised; PhotobookBio runs it again for in-app navigation.
 *
 * @returns {string|null} the token that was moved, if any
 */
export function captureBioToken(win = window) {
  try {
    const { pathname, search, hash } = win.location;
    const token = bioTokenInPath(pathname);
    if (!token) return null;

    rememberBioToken(token, win);
    if (win.history && win.history.replaceState) {
      win.history.replaceState(win.history.state, '', `${visibleBioPath(pathname)}${search}${hash}`);
    }

    return token;
  } catch (e) {
    return null;
  }
}
